import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateMultiAgentRunsList } from "@/hooks/multi-agent";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { isWhitespace, isWordChar, isAlphanumeric } from "@/services/multi-agent";

// ── Entity extraction helpers ──────────────────────────────────────────────

const FU_PREFIXES = [
  "apex_blast_radius", "auto_fix", "report", "weak_test_assertions",
  "integration_reuse_audit", "cleanup_delete_audit", "permission_security_audit",
  "report_dashboard_field_impact", "export markdown", "export pdf", "export word/docx",
];

const FU_ENTITY_SUFFIXES = [
  "Controller", "Trigger", "Handler", "Service", "Batch", "Helper", "Test", "Ctrl",
  "Manager", "Resource", "Selector", "Repository", "Wrapper", "Remoter", "Export",
];

function stripFollowUpPrefix(name: string): string {
  const lower = name.toLowerCase();
  for (const p of FU_PREFIXES) {
    if (lower.startsWith(p)) {
      let i = p.length;
      while (i < name.length && isWhitespace(name[i])) i++;
      if (name[i] === ":") i++;
      while (i < name.length && isWhitespace(name[i])) i++;
      return name.slice(i);
    }
  }
  return name;
}

function isFollowUpEntityToken(token: string): boolean {
  const first = token[0];
  const startsLetter = (first >= "a" && first <= "z") || (first >= "A" && first <= "Z");
  if (startsLetter && (token.endsWith("__c") || token.endsWith("__r"))) return true;
  if (first >= "A" && first <= "Z") {
    for (const suf of FU_ENTITY_SUFFIXES) {
      let idx = token.indexOf(suf);
      while (idx >= 1) {
        let alnumBefore = true;
        for (let k = 0; k < idx; k++) {
          if (!isAlphanumeric(token[k])) { alnumBefore = false; break; }
        }
        if (alnumBefore) return true;
        idx = token.indexOf(suf, idx + 1);
      }
    }
  }
  return false;
}

export function extractFollowUpEntities(md: string, artifacts: { name?: string }[]): string[] {
  const counts = new Map<string, number>();
  const add = (s?: string) => { if (s && s.length > 3) counts.set(s, (counts.get(s) || 0) + 1); };
  const harvest = (text: string) => {
    const t = text || "";
    let i = 0;
    while (i < t.length) {
      if (!isWordChar(t[i])) { i++; continue; }
      let j = i;
      while (j < t.length && isWordChar(t[j])) j++;
      const token = t.slice(i, j);
      i = j;
      if (isFollowUpEntityToken(token)) add(token);
    }
  };
  for (const a of artifacts) harvest(stripFollowUpPrefix(a.name || ""));
  harvest(md);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k).slice(0, 3);
}

// ── Lane-aware follow-up suggestions ──────────────────────────────────────

function followUpsForLane(lane: string | null, entities: string[]): {
  label: string; hint: string; question: string;
}[] {
  const ctxQs = entities.slice(0, 2).flatMap((n, i) =>
    i === 0
      ? [
          { label: `Callers of ${n}`, hint: "Trace what invokes this", question: `Who calls ${n} and how often?` },
          { label: `Tests for ${n}`, hint: "Coverage & gaps", question: `What tests cover ${n} and where are the coverage gaps?` },
        ]
      : [
          { label: `Blast radius of ${n}`, hint: "Full impact tree", question: `What is the full blast radius of changing ${n}?` },
        ],
  );

  const base = (() => {
    switch (lane) {
      case "apex_change":
        return [
          { label: "Blast radius",      hint: "Full impact tree",   question: "What's the full blast radius of this change?" },
          { label: "Test coverage",     hint: "Coverage gaps",      question: "Which tests cover this and where are the coverage gaps?" },
          { label: "Same-object flows", hint: "Related automation", question: "What flows or triggers run against the same object?" },
        ];
      case "risk_audit":
        return [
          { label: "Highest severity",  hint: "Sort & summarize",   question: "Show me the 3 highest-severity items with concrete examples." },
          { label: "Group by object",   hint: "Reorganize",         question: "Re-group the findings by the SObject they affect." },
          { label: "Apex-only risks",   hint: "Narrow scope",       question: "Show me only the Apex risks — drop everything else." },
        ];
      case "flow_change":
        return [
          { label: "Trigger order",     hint: "Pre-flow triggers",  question: "What triggers run before this flow and could conflict?" },
          { label: "Field references",  hint: "Schema deps",        question: "Which fields and validation rules does this flow rely on?" },
        ];
      case "data_model":
        return [
          { label: "Field references",  hint: "Where it's used",    question: "Where else are these fields referenced — code, flows, validations?" },
          { label: "FLS / sharing",     hint: "Permissions impact", question: "Are there FLS or sharing rule implications from this change?" },
        ];
      default:
        return [
          { label: "Show callers",      hint: "Inbound calls",      question: "Who calls the main classes mentioned in this answer?" },
          { label: "Show tests",        hint: "Test coverage",      question: "What tests cover the classes mentioned?" },
          { label: "Recent changes",    hint: "Git history-ish",    question: "What's changed recently in the classes mentioned?" },
        ];
    }
  })();

  return [...ctxQs, ...base].slice(0, 4);
}

// ── Component ──────────────────────────────────────────────────────────────

export function FollowUpChips() {
  const queryClient = useQueryClient();
  const runId = useMultiAgentStore((s) => s.runId);
  const lane = useMultiAgentStore((s) => s.lane);
  const artifacts = useMultiAgentStore((s) => s.artifacts);
  const finalMarkdown = useMultiAgentStore((s) => s.finalMarkdown);
  const backendFollowUps = useMultiAgentStore((s) => s.followUps);

  const entities = useMemo(
    () => extractFollowUpEntities(finalMarkdown, artifacts),
    [finalMarkdown, artifacts],
  );

  const suggestions = backendFollowUps.length
    ? backendFollowUps.slice(0, 4)
    : followUpsForLane(lane, entities);

  const [pending, setPending] = useState<string | null>(null);

  const ask = async (q: string) => {
    if (!runId || pending) return;
    setPending(q);
    try {
      const store = useMultiAgentStore.getState();
      const activeSessionId = store.sessionId || runId;
      const { createRun } = await import("@/services/multi-agent");
      const result = await createRun(q, activeSessionId);
      store.pushTurnToThread();
      store.reset();
      useMultiAgentStore.setState({ sessionId: activeSessionId || result.session_id || result.run_id });
      store.startRun(result.run_id, q);
      await invalidateMultiAgentRunsList(queryClient);
    } catch (e) {
      console.error("refine failed", e);
      toast.error("Couldn't start the follow-up. Please try again.");
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="ma-fade-up mt-10">
      <div className="ma-micro mb-3" style={{ color: "var(--ma-text-muted)" }}>
        Try a follow-up
      </div>
      <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        {suggestions.map((s) => (
          <button
            key={s.label}
            type="button"
            onClick={() => ask(s.question)}
            disabled={pending !== null}
            aria-busy={pending === s.question}
            className="ma-interactive ma-tile flex min-w-0 flex-col items-start gap-1 text-left"
            style={{
              padding: "12px 14px",
              opacity: pending && pending !== s.question ? 0.5 : 1,
            }}
            title={s.question}
          >
            <span className="flex w-full items-center gap-1.5 break-words" style={{ fontSize: 13, fontWeight: 500, color: "var(--ma-text)" }}>
              {pending === s.question && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />}
              {pending === s.question ? "Asking…" : s.label}
            </span>
            <span className="ma-micro w-full break-words" style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)" }}>
              {s.hint}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
